import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const PRICING_FAQS = [
  {
    q: "Y a-t-il un engagement sur les abonnements ?",
    a: "Non, tous nos abonnements sont sans engagement. Vous pouvez résilier à tout moment avec un préavis de 2 mois, sans frais cachés.",
  },
  {
    q: "Comment résilier mon abonnement ?",
    a: "Il suffit de nous envoyer un e-mail à crossfit.saintchamond@gmail.com ou de nous prévenir à la box. La résiliation prend effet 2 mois après votre demande, conformément à nos conditions.",
  },
  {
    q: "Puis-je essayer avant de m'abonner ?",
    a: "Oui ! Nous offrons un cours d'essai gratuit pour découvrir la box, les coachs et l'ambiance. Réservez-le depuis la page Contact ou directement via le bouton « Essai gratuit ».",
  },
  {
    q: "Que comprend le pass Free Access ?",
    a: "Le Free Access est un accès libre à la box (hors cours coachés) pour vos séances en autonomie. Il est inclus dans les abonnements 3×/semaine et Illimité, ou disponible seul.",
  },
  {
    q: "À quoi correspondent les 99€ de frais d'adhésion ?",
    a: "Les frais d'adhésion (99€, payés une seule fois à l'inscription) couvrent l'ouverture de votre dossier, votre bilan d'entrée avec un coach et la remise de votre t-shirt CrossFit Saint-Chamond.",
  },
  {
    q: "Les carnets de séances ont-ils une date de validité ?",
    a: "Oui, chaque carnet est valable 6 mois à compter de l'achat. Ils sont idéaux si vous venez ponctuellement ou si vous voulez tester plusieurs formats (CrossFit WOD, HYROX, Team WOD).",
  },
  {
    q: "Puis-je mettre mon abonnement en pause (vacances, blessure) ?",
    a: "Oui, une suspension d'abonnement est possible pour raison médicale (sur justificatif) ou pour congés prolongés. Contactez-nous pour en faire la demande.",
  },
  {
    q: "Quels moyens de paiement acceptez-vous ?",
    a: "Carte bancaire, virement SEPA et prélèvement automatique mensuel. Certaines entreprises prennent en charge une partie de votre abonnement (sport en entreprise) — demandez-nous un justificatif si besoin.",
  },
];

export const FAQTarifs = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section
      ref={ref}
      data-testid="faq-tarifs-section"
      className="section-padding bg-accent/40"
    >
      <div className="container-custom max-w-3xl">
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-primary font-semibold text-sm tracking-widest uppercase">
            FAQ tarifs
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-dark mt-3 mb-4">
            QUESTIONS SUR LES <span className="text-gradient-primary italic font-display">ABONNEMENTS</span>
          </h2>
          <p className="text-muted-foreground text-sm max-w-xl mx-auto">
            Engagement, résiliation, essai gratuit, frais d'adhésion… Les réponses aux questions qu'on nous pose le plus souvent.
          </p>
        </div>

        <div className="space-y-3">
          {PRICING_FAQS.map((faq, idx) => {
            const open = openIndex === idx;
            return (
              <div
                key={idx}
                data-testid={`faq-tarifs-item-${idx}`}
                className={`border border-border rounded-xl overflow-hidden bg-white transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                } ${open ? 'shadow-md border-primary/40' : ''}`}
                style={{ transitionDelay: `${150 + idx * 70}ms` }}
              >
                <button
                  onClick={() => setOpenIndex(open ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-accent/60 transition-colors"
                  data-testid={`faq-tarifs-toggle-${idx}`}
                  aria-expanded={open}
                >
                  <span className="font-semibold text-base pr-4">{faq.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                      open ? 'rotate-180 text-primary' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    open ? 'max-h-[400px]' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ JSON-LD for SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: PRICING_FAQS.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
};
